"use client";

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from "@gsap/react";
import { SearchList01Icon, SparklesIcon, ZapIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import gsap from "gsap";
import { DrawSVGPlugin, ScrollTrigger, SplitText } from "gsap/all";
import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger, DrawSVGPlugin, SplitText);

/**
 * The about section of the site which highlights my key strengths.
 */
export function AboutSection() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [{
      icon: SparklesIcon,
      title: "Beautiful",
      description: "A beautiful website doesn't just look good. It catches the eye of your client. It should represent your vision in a way that makes people want more. I can help you make that happen."
    }, {
      icon: SearchList01Icon,
      title: "Accessible",
      description: "An accessible website is a successful website. Everyone should have the opportunity to learn about you and what you do. We can help you build a website that follows all the latest regulations in web accessibility."
    }, {
      icon: ZapIcon,
      title: "Fast",
      description: "Speed means everything. People make decisions about you within the first few seconds of opening your site. I will help you build a site that is lightning fast and keeps customers engaged."
    }];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const aboutSteps = t0;
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cardRefs = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const rectRefs = useRef(t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      ScrollTrigger.normalizeScroll(true);
      if (cardRefs.current.length < 3) {
        return;
      }
      const updateRectDimensions = function updateRectDimensions() {
        cardRefs.current.forEach((wrapper, index) => {
          const rect = rectRefs.current[index];
          if (!wrapper || !rect) {
            return;
          }
          rect.setAttribute("width", String(wrapper.offsetWidth - 2));
          rect.setAttribute("height", String(wrapper.offsetHeight - 2));
        });
      };
      updateRectDimensions();
      gsap.set(rectRefs.current, {
        drawSVG: "0%"
      });
      const tl = gsap.timeline({
        defaults: {
          duration: 1,
          ease: "power1.out"
        },
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom bottom",
          pin: ".pinned",
          scrub: 0.5,
          onRefresh: updateRectDimensions
        }
      });
      const headingChars = SplitText.create(".about-heading", {
        type: "chars",
        charsClass: "about-heading-char",
        mask: "chars"
      });
      tl.from(headingChars.chars, {
        y: "100%",
        duration: 0.5,
        stagger: 0.05
      });
      tl.from(cardRefs.current, {
        y: "100%",
        opacity: 0,
        stagger: 0.3
      }, "-=0.5s");
      tl.to(rectRefs.current[0], {
        drawSVG: "100%"
      });
      tl.to(rectRefs.current[1], {
        drawSVG: "100%"
      });
      tl.to(rectRefs.current[2], {
        drawSVG: "100%"
      });
    };
    t4 = {
      scope: sectionRef
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useGSAP(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2 className="about-heading font-bold text-3xl mb-5 xl:text-5xl 2xl:text-7xl 2xl:mb-10">Your website will be...</h2>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <section ref={sectionRef} id="about" className="h-[300vh] 2xl:h-[400vh] pt-18"><div className="pinned">{t5}<div ref={containerRef} className="flex flex-col gap-3 xl:gap-7 2xl:gap-10">{aboutSteps.map((t7, index_0) => {
            const {
              icon,
              title,
              description
            } = t7;
            return <div key={`about-${title}-${index_0}`} ref={el => {
              cardRefs.current[index_0] = el;
            }} className={`relative ${index_0 % 2 !== 0 ? "self-end" : ""} max-w-4xl`}><Card className="gap-2"><CardHeader><CardTitle className="text-sm xl:text-2xl 2xl:text-2xl flex items-center gap-2.5"><HugeiconsIcon icon={icon} className="text-primary 2xl:size-7" />{title}</CardTitle></CardHeader><CardContent className="text-[13px] xl:text-lg">{description}</CardContent></Card><svg className="absolute inset-0 w-full h-full pointer-events-none block overflow-visible"><rect ref={el_0 => {
                  rectRefs.current[index_0] = el_0;
                }} x="1" y="1" rx="12" ry="12" fill="none" className="stroke-primary" strokeWidth="2" /></svg></div>;
          })}</div></div></section>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}